import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Edit2,
  Trash2,
  Tag,
} from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { DateTimePicker } from '@/components/ui/datetime-picker';
import { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@/components/ui/modal';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { Spinner } from '@/components/ui/spinner';
import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import toast from 'react-hot-toast';

// ─── Types ───────────────────────────────────────────────────────────────────

type CouponType = 'flat' | 'percentage' | 'free_delivery';

interface Coupon {
  id: string;
  code: string;
  title: string;
  description?: string;
  type: CouponType;
  value: number;
  max_discount?: number;
  min_order_value: number;
  max_uses_total?: number;
  max_uses_per_user: number;
  used_count: number;
  valid_from: string;
  valid_until?: string;
  applicable_vehicle_types?: string[];
  is_first_ride_only: boolean;
  is_active: boolean;
  created_at: string;
}

// ─── Schema ───────────────────────────────────────────────────────────────────

const couponSchema = z.object({
  code:                    z.string().min(1).max(50).toUpperCase(),
  title:                   z.string().min(1).max(100),
  description:             z.string().optional(),
  type:                    z.enum(['flat', 'percentage', 'free_delivery']),
  value:                   z.coerce.number().min(0),
  max_discount:            z.coerce.number().min(0).optional().or(z.literal('')),
  min_order_value:         z.coerce.number().min(0).default(0),
  max_uses_total:          z.coerce.number().int().min(1).optional().or(z.literal('')),
  max_uses_per_user:       z.coerce.number().int().min(1).default(1),
  valid_from:              z.string().min(1, 'Required'),
  valid_until:             z.string().optional(),
  applicable_vehicle_types: z.array(z.string()).optional(),
  is_first_ride_only:      z.boolean().default(false),
  is_active:               z.boolean().default(true),
});

type CouponFormData = z.infer<typeof couponSchema>;

// Matches the backend's VehicleType enum (vehicle.entity.ts) exactly — a
// coupon restricted to "bike" must only ever apply to real bike bookings.
const VEHICLE_TYPE_OPTIONS = [
  { value: 'bike', label: 'Bike' },
  { value: 'scooty', label: 'Scooty' },
  { value: 'auto', label: 'Auto' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'mini_truck', label: 'Mini Truck' },
  { value: 'three_wheeler', label: 'Three Wheeler' },
  { value: 'tata_ace', label: 'Tata Ace' },
  { value: 'pickup_8ft', label: 'Pickup 8ft' },
  { value: 'tata_407', label: 'Tata 407' },
];
const VEHICLE_LABEL: Record<string, string> = Object.fromEntries(VEHICLE_TYPE_OPTIONS.map((v) => [v.value, v.label]));

// ─── API helpers ──────────────────────────────────────────────────────────────

const fetchCoupons = async (): Promise<Coupon[]> => {
  const { data } = await api.get(API_ENDPOINTS.COUPONS.LIST);
  const result = data.data ?? [];
  return Array.isArray(result) ? result : [];
};

const createCoupon = async (payload: CouponFormData) => {
  const { data } = await api.post(API_ENDPOINTS.COUPONS.CREATE, payload);
  return data.data;
};

const updateCoupon = async ({ id, ...payload }: Partial<Coupon> & { id: string }) => {
  const { data } = await api.put(API_ENDPOINTS.COUPONS.UPDATE(id), payload);
  return data.data;
};

const deleteCoupon = async (id: string) => {
  await api.delete(API_ENDPOINTS.COUPONS.DELETE(id));
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<CouponType, string> = {
  flat:          'Flat ₹ Off',
  percentage:    '% Off',
  free_delivery: 'Free Delivery',
};

const TYPE_COLORS: Record<CouponType, 'default' | 'success' | 'warning'> = {
  flat:          'default',
  percentage:    'success',
  free_delivery: 'warning',
};

const fmtDate = (iso: string) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const isExpired = (c: Coupon) => c.valid_until && new Date(c.valid_until) < new Date();

function discountLabel(c: Coupon) {
  if (c.type === 'flat')          return `₹${c.value} off`;
  if (c.type === 'percentage')    return `${c.value}%${c.max_discount ? ` (max ₹${c.max_discount})` : ''}`;
  if (c.type === 'free_delivery') return 'Free delivery';
  return '';
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CouponsPage() {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<{ open: boolean; coupon: Coupon | null }>({ open: false, coupon: null });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; coupon: Coupon | null }>({ open: false, coupon: null });

  const { data: coupons = [], isLoading } = useQuery({ queryKey: ['coupons'], queryFn: fetchCoupons });

  const { register, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } = useForm<CouponFormData>({
    resolver: zodResolver(couponSchema),
    defaultValues: {
      type: 'flat', value: 0, min_order_value: 0, max_uses_per_user: 1,
      is_first_ride_only: false, is_active: true,
    },
  });

  const couponType = watch('type');

  const openCreate = () => {
    reset({
      type: 'flat', value: 0, min_order_value: 0, max_uses_per_user: 1,
      is_first_ride_only: false, is_active: true,
      valid_from: new Date().toISOString(),
      applicable_vehicle_types: [],
    });
    setModal({ open: true, coupon: null });
  };

  const openEdit = (c: Coupon) => {
    reset({
      code:                    c.code,
      title:                   c.title,
      description:             c.description ?? '',
      type:                    c.type,
      value:                   c.value,
      max_discount:            c.max_discount ?? ('' as any),
      min_order_value:         c.min_order_value,
      max_uses_total:          c.max_uses_total ?? ('' as any),
      max_uses_per_user:       c.max_uses_per_user,
      valid_from:              c.valid_from,
      valid_until:             c.valid_until ?? '',
      applicable_vehicle_types: c.applicable_vehicle_types ?? [],
      is_first_ride_only:      c.is_first_ride_only,
      is_active:               c.is_active,
    });
    setModal({ open: true, coupon: c });
  };

  const createMutation = useMutation({
    mutationFn: createCoupon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      toast.success('Coupon created');
      setModal({ open: false, coupon: null });
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateCoupon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      toast.success('Coupon updated');
      setModal({ open: false, coupon: null });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCoupon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      toast.success('Coupon deleted');
      setDeleteDialog({ open: false, coupon: null });
    },
  });

  const onSubmit = (formData: CouponFormData) => {
    const payload = {
      ...formData,
      code:                    formData.code.toUpperCase().trim(),
      max_discount:            formData.max_discount === '' ? undefined : Number(formData.max_discount),
      max_uses_total:          formData.max_uses_total === '' ? undefined : Number(formData.max_uses_total),
      applicable_vehicle_types: formData.applicable_vehicle_types?.length ? formData.applicable_vehicle_types : undefined,
    };

    if (modal.coupon) {
      updateMutation.mutate({ id: modal.coupon.id, ...payload } as any);
    } else {
      createMutation.mutate(payload as any);
    }
  };

  const toggleActive = (c: Coupon) => {
    updateMutation.mutate({ id: c.id, is_active: !c.is_active } as any);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Coupons & Offers"
        description="Create and manage promo codes for customers"
        actions={
          <Button onClick={openCreate} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            New Coupon
          </Button>
        }
      />

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Coupons',   value: coupons.length,                        color: 'text-blue-600' },
          { label: 'Active',          value: coupons.filter(c => c.is_active && !isExpired(c)).length, color: 'text-green-600' },
          { label: 'Expired',         value: coupons.filter(c => isExpired(c)).length, color: 'text-red-500' },
          { label: 'Total Redeemed',  value: coupons.reduce((s, c) => s + c.used_count, 0), color: 'text-purple-600' },
        ].map(({ label, value, color }) => (
          <Card key={label}>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Table ── */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center p-12"><Spinner /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Min Order</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Validity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coupons.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-10 text-gray-400">
                      No coupons yet. Create your first one!
                    </TableCell>
                  </TableRow>
                )}
                {coupons.map(coupon => (
                  <TableRow key={coupon.id} className={!coupon.is_active || isExpired(coupon) ? 'opacity-50' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4 text-gray-400" />
                        <span className="font-mono font-semibold text-blue-600">{coupon.code}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{coupon.title}</p>
                        {coupon.is_first_ride_only && (
                          <span className="text-xs text-amber-600 font-medium">First ride only</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={TYPE_COLORS[coupon.type]}>{TYPE_LABELS[coupon.type]}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{discountLabel(coupon)}</TableCell>
                    <TableCell>
                      {coupon.applicable_vehicle_types?.length ? (
                        <div className="flex flex-wrap gap-1 max-w-[160px]">
                          {coupon.applicable_vehicle_types.map((vt) => (
                            <Badge key={vt} variant="outline" className="text-xs">
                              {VEHICLE_LABEL[vt] ?? vt}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">All vehicles</span>
                      )}
                    </TableCell>
                    <TableCell>{coupon.min_order_value > 0 ? `₹${coupon.min_order_value}` : '—'}</TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {coupon.used_count}
                        {coupon.max_uses_total ? ` / ${coupon.max_uses_total}` : ''}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs">
                        <p>From: {fmtDate(coupon.valid_from)}</p>
                        <p className={isExpired(coupon) ? 'text-red-500' : ''}>
                          {coupon.valid_until ? `Until: ${fmtDate(coupon.valid_until)}` : 'No expiry'}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {isExpired(coupon) ? (
                        <Badge variant="destructive">Expired</Badge>
                      ) : (
                        <Switch
                          checked={coupon.is_active}
                          onChange={() => toggleActive(coupon)}
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(coupon)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => setDeleteDialog({ open: true, coupon })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Create / Edit Modal ── */}
      <Modal isOpen={modal.open} onClose={() => setModal({ open: false, coupon: null })} className="max-w-2xl">
        <ModalHeader>
          <ModalTitle>{modal.coupon ? 'Edit Coupon' : 'Create New Coupon'}</ModalTitle>
        </ModalHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody className="space-y-4">
            {/* Code + Title */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Code *</Label>
                <Input {...register('code')} placeholder="FIRST50" className="uppercase" disabled={!!modal.coupon} />
                {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code.message}</p>}
              </div>
              <div>
                <Label>Title *</Label>
                <Input {...register('title')} placeholder="Get ₹50 off your first ride" />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>}
              </div>
            </div>

            {/* Description */}
            <div>
              <Label>Description</Label>
              <Input {...register('description')} placeholder="Optional details shown to customer" />
            </div>

            {/* Type */}
            <div>
              <Label>Coupon Type *</Label>
              <div className="flex gap-3 mt-1">
                {(['flat', 'percentage', 'free_delivery'] as CouponType[]).map(t => (
                  <label key={t} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value={t}
                      {...register('type')}
                      className="text-blue-600"
                    />
                    <span className="text-sm">{TYPE_LABELS[t]}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Value + Max Discount */}
            <div className="grid grid-cols-2 gap-4">
              {couponType !== 'free_delivery' && (
                <div>
                  <Label>{couponType === 'flat' ? 'Flat Discount (₹) *' : 'Percentage (%) *'}</Label>
                  <Input type="number" step="0.01" {...register('value')} />
                  {errors.value && <p className="text-red-500 text-xs mt-1">{errors.value.message}</p>}
                </div>
              )}
              {couponType === 'percentage' && (
                <div>
                  <Label>Max Discount Cap (₹)</Label>
                  <Input type="number" step="0.01" {...register('max_discount')} placeholder="e.g. 100" />
                </div>
              )}
            </div>

            {/* Min order + Per user limit */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Order Value (₹)</Label>
                <Input type="number" step="0.01" {...register('min_order_value')} placeholder="0" />
              </div>
              <div>
                <Label>Max Uses Per User</Label>
                <Input type="number" {...register('max_uses_per_user')} placeholder="1" />
              </div>
            </div>

            {/* Total uses */}
            <div>
              <Label>Max Total Uses (blank = unlimited)</Label>
              <Input type="number" {...register('max_uses_total')} placeholder="e.g. 500" />
            </div>

            {/* Validity */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valid From *</Label>
                <DateTimePicker
                  value={watch('valid_from') ? new Date(watch('valid_from')) : undefined}
                  onChange={(d) => setValue('valid_from', d ? d.toISOString() : '', { shouldValidate: true })}
                  placeholder="Select date"
                />
                {errors.valid_from && <p className="text-red-500 text-xs mt-1">{errors.valid_from.message}</p>}
              </div>
              <div>
                <Label>Valid Until (blank = no expiry)</Label>
                <DateTimePicker
                  value={watch('valid_until') ? new Date(watch('valid_until')!) : undefined}
                  onChange={(d) => setValue('valid_until', d ? d.toISOString() : '')}
                  placeholder="No expiry"
                />
              </div>
            </div>

            {/* Vehicle types */}
            <div>
              <Label>Applicable Vehicle Types (blank = all)</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {VEHICLE_TYPE_OPTIONS.map((vt) => {
                  const selected = watch('applicable_vehicle_types') ?? [];
                  const checked = selected.includes(vt.value);
                  return (
                    <label key={vt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...selected, vt.value]
                            : selected.filter((v) => v !== vt.value);
                          setValue('applicable_vehicle_types', next);
                        }}
                        className="rounded border-gray-300 text-blue-600"
                      />
                      {vt.label}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Toggles */}
            <div className="flex items-center gap-8">
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch
                  checked={watch('is_first_ride_only')}
                  onChange={(e) => setValue('is_first_ride_only', e.target.checked)}
                />
                <span className="text-sm font-medium">First ride only</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch
                  checked={watch('is_active')}
                  onChange={(e) => setValue('is_active', e.target.checked)}
                />
                <span className="text-sm font-medium">Active</span>
              </label>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button type="button" variant="outline" onClick={() => setModal({ open: false, coupon: null })}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}>
              {(isSubmitting || createMutation.isPending || updateMutation.isPending)
                ? <Spinner size="sm" />
                : modal.coupon ? 'Save Changes' : 'Create Coupon'
              }
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* ── Delete Dialog ── */}
      <ConfirmDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, coupon: null })}
        onConfirm={() => { if (deleteDialog.coupon) deleteMutation.mutate(deleteDialog.coupon.id); }}
        title="Delete Coupon"
        description={`Are you sure you want to delete coupon "${deleteDialog.coupon?.code}"? This cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
